import { EmbeddedMessage } from 'constants/messageMediums';
import { CampaignStep } from 'modules/campaigns/editor/types';
import { CAMPAIGN_TEMPLATE_EDITABLE_STATES } from 'modules/campaigns/lib/constants';
import { pluralizeWord } from 'modules/campaigns/lib/utils';
import { useTemplateTakeover } from 'modules/templateEditor/integrations/InStudioTemplateEditing/context';
import { ActionMenu } from 'ui/actionMenu';
import {
  ActionMenuOption,
  AllowedActionMenuElements,
  MENU_ALIGNMENTS,
  MENU_DIRECTIONS,
} from 'ui/actionMenu/types';
import { Button, BUTTON_SIZES, BUTTON_VARIANTS } from 'ui/button';
import { CollapsibleContentArrow } from 'ui/collapsibleContent';
import { ChevronDown, Edit16 } from 'ui/icons';
import { Tooltip } from 'ui/tooltip';
import { useQueryParams } from 'utils/hooks/useQueryParams';
import { CAMPAIGN_STEP_NAMES_MAPPER } from '../../constants';
import {
  useCampaignContext,
  useEditorContext,
  useTemplateContext,
  useUnsavedPromptContext,
} from '../../context';
import { StyledCounterBadge } from '../../styles';
import { getIsCampaignStepOptional, getIsDefaultWhatsAppTemplate } from '../utils';
import {
  CampaignCardHeader,
  CampaignCardHeaderTitleWrapper,
  CampaignCardTitle,
  CampaignCardTitleDetail,
  CampaignCardWrapper,
  CampaignStepCardHeaderTitle,
  CampaignStepCardWrapper,
  ComboButton,
  FullWidthCollapsibleContent,
  StyledOverflowButton,
} from './CampaignCard.styled';
import { CampaignCardActions } from './CampaignCardActions';
import { CampaignContentCardState, CampaignStepCardState } from './CampaignCardState';
import { getCampaignEditorSections, shouldExcludeConversionsStep } from './utils';

import { useFeatureFlag } from 'utils/hooks/useFeatureFlag';
import { ConversionSection } from '../Sections';

const CONTENT_EDIT_BUTTON_TOOLTIP_TEST_ID = 'content-edit-button-tooltip-test-id';
export const CONTENT_EDIT_BUTTON_TEST_ID = 'content-edit-button-test-id';

export const CampaignStepCard = ({
  children,
  campaignStep,
}: {
  campaignStep: CampaignStep;
  children: any;
}) => {
  const { campaignSteps, viewCampaignStep, closeCampaignStep } = useEditorContext();
  const { transitionToCampaignStep } = useUnsavedPromptContext();
  const campaignState = campaignSteps[campaignStep];
  const isCollapsed = !campaignState?.isOpen;
  const headerClickHandler = !isCollapsed
    ? () => transitionToCampaignStep(campaignStep, () => closeCampaignStep(campaignStep))
    : () => transitionToCampaignStep(campaignStep, () => viewCampaignStep(campaignStep));
  return (
    <CampaignStepCardWrapper $isEditable={campaignState?.isEditable}>
      <CampaignCardHeader data-test={`${CAMPAIGN_STEP_NAMES_MAPPER[campaignStep]}-card-header`}>
        <CampaignCardHeaderTitleWrapper>
          <CampaignStepCardState campaignStep={campaignStep} />
          <CampaignStepCardHeaderTitle onClick={headerClickHandler}>
            <CampaignCardTitle>{CAMPAIGN_STEP_NAMES_MAPPER[campaignStep]} </CampaignCardTitle>
            {getIsCampaignStepOptional(campaignStep) && (
              <CampaignCardTitleDetail> (optional)</CampaignCardTitleDetail>
            )}
            <CollapsibleContentArrow isCollapsed={isCollapsed} height={20} width={20} />
          </CampaignStepCardHeaderTitle>
        </CampaignCardHeaderTitleWrapper>
        <CampaignCardActions campaignStep={campaignStep} />
      </CampaignCardHeader>
      <FullWidthCollapsibleContent isCollapsed={isCollapsed}>
        {children}
      </FullWidthCollapsibleContent>
    </CampaignStepCardWrapper>
  );
};

export const CampaignEditableContent = () => {
  const { campaign } = useCampaignContext();
  const isWhatsAppGAEnabled = useFeatureFlag('WhatsAppMessageChannelGA');
  const isEmbeddedCampaign = campaign.messageMedium === EmbeddedMessage;

  const query = useQueryParams();
  const showConversionsForEmbedded = query.get('__embeddedConversionEnabled');

  // Temporarily add conversion section for Embedded Campaigns enabled by query param to allow
  // for product testing around Conversion enablement for Embedded
  const sections = {
    ...getCampaignEditorSections(campaign.campaignTypeDefinition, campaign.messageMedium),
    ...(isEmbeddedCampaign && showConversionsForEmbedded
      ? { [CampaignStep.ConversionAndExperiment]: ConversionSection }
      : {}),
  };

  // TODO: Remove this check after WhatsApp GA release
  if (shouldExcludeConversionsStep(campaign.messageMedium, isWhatsAppGAEnabled)) {
    delete sections[CampaignStep.Conversions];
  }

  return (
    <>
      {Object.entries(sections).map(([campaignStep, EditorSection]: any, idx: number) => {
        return (
          <CampaignStepCard key={`${campaignStep}-${idx}`} campaignStep={campaignStep}>
            <EditorSection />
          </CampaignStepCard>
        );
      })}
    </>
  );
};

export const CampaignContentCard = ({
  children,
  $isTemplateContent = false,
}: {
  children?: any;
  $isTemplateContent?: boolean;
}) => {
  const { campaign } = useCampaignContext();
  const { canDraftMessaging } = useEditorContext();
  const takeover = useTemplateTakeover();
  const {
    templateState: { isDefaultTemplate, errorCount },
    templateAndContent: { templateType },
  } = useTemplateContext();

  const setViewOptions: ActionMenuOption[] = [
    {
      as: AllowedActionMenuElements.Button,
      content: 'Replace template',
      id: 'replace-template',
      onClick: () => takeover.open(takeover.viewOptions.select),
    },
  ];

  /* Check for both permission to edit campaigns and for content errors.
  Permission message takes precedence over content error message. */
  const hasTooltip = !canDraftMessaging || errorCount > 0;
  const tooltipContent = !canDraftMessaging
    ? 'You do not have permission to edit campaigns'
    : `${errorCount} content ${pluralizeWord('locale', errorCount)} errors`;

  const isDefaultWhatsAppTemplate = getIsDefaultWhatsAppTemplate(
    campaign.messageMedium,
    templateType,
  );
  const shouldShowEditButton =
    !isDefaultWhatsAppTemplate &&
    !isDefaultTemplate &&
    CAMPAIGN_TEMPLATE_EDITABLE_STATES.includes(campaign?.campaignState);

  return (
    <CampaignCardWrapper $isTemplateContent={$isTemplateContent}>
      <CampaignCardHeader $isTemplateContent={$isTemplateContent}>
        <CampaignCardHeaderTitleWrapper>
          <CampaignContentCardState />
          <CampaignCardTitle>{CAMPAIGN_STEP_NAMES_MAPPER[CampaignStep.Content]}</CampaignCardTitle>
        </CampaignCardHeaderTitleWrapper>
        {shouldShowEditButton && (
          <Tooltip isEnabled={hasTooltip} tooltipContent={tooltipContent}>
            <ComboButton data-test={CONTENT_EDIT_BUTTON_TOOLTIP_TEST_ID}>
              <Button
                isDisabled={!canDraftMessaging}
                data-test={CONTENT_EDIT_BUTTON_TEST_ID}
                size={BUTTON_SIZES.SMALL}
                variant={BUTTON_VARIANTS.SECONDARY}
                onClick={() => takeover.open(takeover.viewOptions.settings)}
              >
                <Edit16 />
                Edit
                {errorCount > 0 && <StyledCounterBadge>{errorCount}</StyledCounterBadge>}
              </Button>
              <ActionMenu
                size={BUTTON_SIZES.SMALL}
                align={MENU_ALIGNMENTS.RIGHT}
                direction={MENU_DIRECTIONS.BOTTOM}
                options={setViewOptions}
              >
                <StyledOverflowButton
                  isDisabled={!canDraftMessaging}
                  data-test={'edit-content-action-menu'}
                  size={BUTTON_SIZES.SMALL}
                  variant={BUTTON_VARIANTS.SECONDARY}
                >
                  <ChevronDown height={16} width={16} />
                </StyledOverflowButton>
              </ActionMenu>
            </ComboButton>
          </Tooltip>
        )}
      </CampaignCardHeader>
      {children}
    </CampaignCardWrapper>
  );
};
